/**
 * Traffic Step Constants
 * ======================
 * Extracted constants from TrafficStep.js for better modularity.
 */
import { Wand2, Upload, Subtitles, Mic, User } from 'lucide-react';

// Ad types available for creation
export const TIPO_ANUNCIO = [
  { id: 'generar_imagen', nombre: 'Generar imagen con IA', icon: Wand2, descripcion: 'Elige entre varios motores de IA', creditos: null, badge: 'Recomendado' },
  { id: 'subir_imagen', nombre: 'Subir imagen propia', icon: Upload, descripcion: 'Usa tu foto y añadimos el headline encima', creditos: 12, badge: null },
  { id: 'subir_video_subtitulos', nombre: 'Subir vídeo + subtítulos', icon: Subtitles, descripcion: 'Sube tu vídeo y añadimos subtítulos dinámicos', creditos: 12, badge: null },
  { id: 'subir_video_voz', nombre: 'Subir vídeo + voz IA', icon: Mic, descripcion: 'Vídeo mudo + guión = vídeo con voz profesional', creditos: 22, badge: null },
  { id: 'avatar_ia', nombre: 'Avatar IA hablando', icon: User, descripcion: 'Avatar con voz y lip-sync real', creditos: null, badge: 'Voz + Video' },
];

// Image generation engines
export const MOTORES_IMAGEN = [
  {
    id: 'flux_pro',
    nombre: 'Flux Pro',
    descripcion: 'Fotorrealista premium',
    badge: null,
    creditos: 11,
    endpoint: '/ai/generate-creative-image',
    disponible: true,
    provider: 'fal.ai',
    supportsNegative: true,
    bestFor: ['photorealistic', 'commercial', 'lifestyle', 'editorial']
  },
  {
    id: 'flux_schnell', 
    nombre: 'Flux Schnell',
    descripcion: 'Rápido · Borradores',
    badge: '⚡ Más rápido',
    creditos: 6,
    endpoint: '/ai/generate-creative-image',
    disponible: true,
    provider: 'fal.ai',
    supportsNegative: true,
    bestFor: ['draft', 'variation', 'quick', 'test']
  },
  {
    id: 'nano_banana',
    nombre: 'Nano Banana',
    descripcion: 'Control de marca · Logos',
    badge: '🎨 Marca',
    creditos: 6,
    endpoint: '/ai/generate-creative-image',
    disponible: true,
    provider: 'Gemini',
    supportsNegative: false,
    bestFor: ['brand', 'logo', 'reference', 'consistent']
  },
  {
    id: 'seedream',
    nombre: 'Seedream V4',
    descripcion: 'Ilustración · Flat design',
    badge: '🖼️ Ilustración',
    creditos: 8,
    endpoint: '/ai/generate-creative-image',
    disponible: true,
    provider: 'Higgsfield',
    supportsNegative: true,
    bestFor: ['illustration', 'flat', 'graphic', 'abstract']
  },
  {
    id: 'gpt_image',
    nombre: 'GPT Image',
    descripcion: 'OpenAI (manual)',
    badge: '🤖 OpenAI',
    creditos: 10,
    endpoint: '/ai/higgsfield/image',
    disponible: true,
    provider: 'OpenAI',
    model: 'gpt_image',
    supportsNegative: false,
    bestFor: ['photorealistic', 'creative', 'artistic']
  }
];

// Video generation engines
export const MOTORES_VIDEO = [
  {
    id: 'kling_pro',
    nombre: 'Kling Pro',
    descripcion: 'Máxima calidad · 5s',
    badge: '🏆 Mejor calidad',
    creditos: 22,
    endpoint: '/ai/generate/kling-avatar',
    disponible: true,
    provider: 'fal.ai'
  },
  {
    id: 'kling_turbo',
    nombre: 'Kling Turbo',
    descripcion: 'Más rápido · 5s',
    badge: '⚡ Más rápido',
    creditos: 15,
    endpoint: '/ai/generate/kling-turbo',
    disponible: true,
    provider: 'fal.ai'
  },
  {
    id: 'hf_kling',
    nombre: 'Kling 1.6 Pro',
    descripcion: 'Vía Higgsfield · 5s',
    badge: '🚀 Higgsfield',
    creditos: 25,
    endpoint: '/ai/higgsfield/video',
    disponible: true,
    provider: 'Higgsfield',
    model: 'kling'
  },
  {
    id: 'hf_kling_turbo',
    nombre: 'Kling 2 Turbo',
    descripcion: 'Rápido vía Higgsfield',
    badge: '⚡ HF Turbo',
    creditos: 18,
    endpoint: '/ai/higgsfield/video',
    disponible: true,
    provider: 'Higgsfield',
    model: 'kling_turbo'
  },
  {
    id: 'hf_veo3',
    nombre: 'Veo 3',
    descripcion: 'Google Veo con audio',
    badge: '🎬 Google',
    creditos: 65,
    endpoint: '/ai/higgsfield/video',
    disponible: true,
    provider: 'Higgsfield',
    model: 'veo3'
  }
];

// Voice synthesis engines
export const MOTORES_VOZ = [
  {
    id: 'elevenlabs',
    nombre: 'ElevenLabs',
    descripcion: 'Voz ultra realista',
    badge: '🎙️ Premium',
    creditos: 10,
    endpoint: '/ai/text-to-speech',
    disponible: true
  },
  {
    id: 'gemini_tts',
    nombre: 'Gemini TTS',
    descripcion: 'Voz natural económica',
    badge: '💚 Económica',
    creditos: 5,
    endpoint: '/ai/generate/gemini-tts',
    disponible: true
  }
];

// Avatar generation engines
export const MOTORES_AVATAR = [
  {
    id: 'heygen',
    nombre: 'HeyGen',
    descripcion: 'Avatar profesional HD',
    badge: '👤 Premium',
    creditos: 54,
    endpoint: '/ai/generate/heygen-avatar',
    disponible: true,
    provider: 'HeyGen'
  },
  {
    id: 'hf_speak',
    nombre: 'Speak 2.0',
    descripcion: 'Lip-sync 720p · Higgsfield',
    badge: '🚀 Higgsfield',
    creditos: 35,
    endpoint: '/ai/higgsfield/avatar',
    disponible: true,
    provider: 'Higgsfield',
    model: 'speak'
  },
  {
    id: 'hf_speak_hd',
    nombre: 'Speak 2.0 HD',
    descripcion: 'Lip-sync 1080p/48fps',
    badge: '🎬 HF Premium',
    creditos: 50,
    endpoint: '/ai/higgsfield/avatar',
    disponible: true,
    provider: 'Higgsfield',
    model: 'speak_hd'
  }
];

// Subtitle styles
export const ESTILOS_SUBTITULOS = [
  { id: 'hormozi', nombre: 'Hormozi', descripcion: 'Bold con highlight' },
  { id: 'minimal', nombre: 'Minimal', descripcion: 'Limpio y elegante' },
  { id: 'bold', nombre: 'Bold', descripcion: 'Grande e impactante' },
];

// Color mapping for ad angles/types
export const ANGULO_COLORS = {
  'Transformación': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  'Dolor': 'bg-red-500/20 text-red-400 border-red-500/30',
  'Resultado': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'Comunidad': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  'Urgencia': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'ROI': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  'Tiempo': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  'Competencia': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  'Autoridad': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
  'Escasez': 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  'Oferta': 'bg-green-500/20 text-green-400 border-green-500/30',
  'Exclusividad': 'bg-violet-500/20 text-violet-400 border-violet-500/30',
  'Social Proof': 'bg-sky-500/20 text-sky-400 border-sky-500/30',
  'Problema': 'bg-red-500/20 text-red-400 border-red-500/30',
  'Solución': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  'Prueba Social': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'Curiosidad': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
};

// Format display information for carousel
export const FORMAT_INFO = {
  '4:5': { id: 'feed', nombre: 'Feed', aspect: 'aspect-[4/5]', legacy: 'creativo_feed_url' },
  '9:16': { id: 'story', nombre: 'Story/Reel', aspect: 'aspect-[9/16]', legacy: 'creativo_story_url' },
  '1:1': { id: 'cuadrado', nombre: 'Cuadrado', aspect: 'aspect-square', legacy: null },
  '1.91:1': { id: 'horizontal', nombre: 'Horizontal', aspect: 'aspect-[1.91/1]', legacy: null },
  '16:9': { id: 'video_16_9', nombre: 'Video 16:9', aspect: 'aspect-video', legacy: null }
};
